<template>
    <v-dialog v-model="dialog" width="1200">
        <template v-slot:activator="{props}">
            <div class="ms-6 d-flex align-center gc-1">
                <span class="text-caption">
                    {{ `${$t('edit')} ${$t('itinerary')}`.toLowerCase() }}
                </span>
                <v-btn variant="text" size="x-small" v-bind="props" icon="mdi-pencil" />
            </div>
        </template>
        <v-card>
            <v-icon class="v-card-close mt-3 me-3" @click="dialog = false">mdi-close</v-icon>
            <v-card-title>
                {{ `${$t('edit')} ${$t('itinerary')}` }}
            </v-card-title>
            <v-card-text>
                <v-tabs v-model="tab" show-arrows>
                    <v-tab v-for="item in tabItems" :key="item" :disabled="!duration">
                        {{ $t(item) }}
                    </v-tab>
                </v-tabs>
                <v-card variant="outlined">
                    <v-form ref="form" v-model="valid">
                        <v-card-text>
                            <v-expand-transition>
                                <v-tabs
                                    v-if="tabItems[tab] !== 'photo'"
                                    v-model="locale"
                                    align-tabs="center"
                                    fixed-tabs
                                    show-arrows
                                >
                                    <v-tab v-for="item in locales" :key="item">
                                        <language-label :language="item" text-button translate />
                                    </v-tab>
                                </v-tabs>
                            </v-expand-transition>
                            <v-window v-model="tab">
                                <v-window-item>
                                    <v-row class="mt-4">
                                        <v-col :cols="2">
                                            <v-text-field
                                                v-model.number="duration"
                                                density="compact"
                                                variant="outlined"
                                                :label="$t('duration')"
                                                type="number"
                                                :rules="[requiredRule('duration')]"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row no-gutters>
                                        <v-col :cols="12">
                                            <v-textarea
                                                v-model="productDescriptions[locales[locale]]"
                                                rows="3"
                                                density="compact"
                                                variant="outlined"
                                                :label="$t('description')"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row no-gutters>
                                        <v-col :cols="12">
                                            <v-textarea
                                                v-model="includedInfoDescriptions[locales[locale]]"
                                                rows="3"
                                                density="compact"
                                                variant="outlined"
                                                :label="$t('included')"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row no-gutters>
                                        <v-col :cols="12">
                                            <v-textarea
                                                v-model="notIncludedInfoDescriptions[locales[locale]]"
                                                rows="3"
                                                density="compact"
                                                variant="outlined"
                                                :label="$t('not_included')"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-window-item>
                                <v-window-item>
                                    <v-row v-for="day in duration" :key="day" no-gutters>
                                        <v-col cols="12">
                                            <v-card>
                                                <v-card-title color="secondary" class="text-capitalize">
                                                    {{ `${$t('day', 1)}  ${day}` }}
                                                </v-card-title>
                                                <v-card-text>
                                                    <v-col :cols="12">
                                                        <v-textarea
                                                            v-model="
                                                                itineraryDaysDescriptions[day - 1].descriptions[
                                                                    locales[locale]
                                                                ]
                                                            "
                                                            rows="2"
                                                            density="compact"
                                                            variant="outlined"
                                                            :label="$t('description')"
                                                        />
                                                    </v-col>
                                                </v-card-text>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-window-item>
                                <v-window-item>
                                    <images-upload-form v-model="images" class="pt-6">
                                        <template v-slot:fields="{image}">
                                            <v-text-field
                                                v-model="image.dayNumber"
                                                persistent-placeholder
                                                density="compact"
                                                variant="outlined"
                                                :label="$t('day', 1)"
                                            />
                                        </template>
                                    </images-upload-form>
                                </v-window-item>
                            </v-window>
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer />
                            <v-btn color="primary" :loading="loading" :disabled="!valid" @click="save">
                                {{ 'save' }}
                            </v-btn>
                        </v-card-actions>
                    </v-form>
                </v-card>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, Watch} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import LanguageLabel from '@/components/snippets/LanguageLabel.vue'
    import ImagesUploadForm from '~/components/account/snippets/ImagesUploadForm.vue'

    @Component({
        components: {LanguageLabel, ImagesUploadForm},
    })
    export class EditItineraryModal extends mixins(FormValidationRulesMixin) {
        @Prop() orderId
        @Prop() orderItinerary

        dialog = false
        loading = false
        tabItems = ['general_info', 'description', 'photo']
        tab = 0
        locale = 0
        fileInputKey = 0
        valid = false

        duration = 0
        productDescriptions = {}
        includedInfoDescriptions = {}
        notIncludedInfoDescriptions = {}
        itineraryDaysDescriptions = null
        images = []

        @Watch('dialog')
        onDialogChange() {
            this.reset()
        }

        @Watch('duration')
        onDurationChange() {
            this.itineraryDaysDescriptions = [...Array(this.duration)].map((e, i) => ({
                number: this.itineraryDaysDescriptions[i]?.number || i + 1,
                descriptions: this.itineraryDaysDescriptions[i]?.descriptions || {},
            }))
        }

        @Emit()
        change() {}

        async onPhotoLoad(files) {
            for (let i = 0; i < files.length; i++) {
                const base64Img = await this.toBase64(files[i])

                this.images.push({
                    order: this.images.length + 1,
                    fileStream: base64Img,
                    toDelete: false,
                })
            }
        }

        async save() {
            try {
                this.loading = true
                await this.$api.orderItinerary.put(this.request)
                this.change()
            } catch (e) {
                if (e.msg) {
                    this.$toast.error(e.msg)
                }
            } finally {
                this.loading = false
            }
        }

        toggleDeleteImage(image) {
            image.toDelete = !image.toDelete
        }

        onImageDisplayNumberChange(event, image, index) {
            const newImgDisplayNumber = +event
            const prevImgDisplayNumber = index + 1

            this.images.forEach(img => {
                if (newImgDisplayNumber < prevImgDisplayNumber) {
                    if (+img.order >= newImgDisplayNumber && img !== image && !(+img.order > prevImgDisplayNumber)) {
                        img.order = +img.order + 1
                    }
                } else {
                    if (+img.order <= newImgDisplayNumber && img !== image && !(+img.order < prevImgDisplayNumber)) {
                        img.order = +img.order - 1
                    }
                }
            })

            this.images.sort((img1, img2) => {
                return img1.order - img2.order
            })
        }

        toBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => resolve(reader.result)
                reader.onerror = error => reject(error)
            })
        }

        reset() {
            this.duration = this.orderItinerary.duration
            this.productDescriptions = this.orderItinerary.productDescription || {}
            this.includedInfoDescriptions = this.orderItinerary.includedInfo || {}
            this.notIncludedInfoDescriptions = this.orderItinerary.notIncludedInfo || {}
            this.itineraryDaysDescriptions =
                this.orderItinerary.itineraryDays?.map(e => ({
                    number: e.number,
                    descriptions: e.description || {},
                })) || {}
            this.fileInputKey++
            this.images =
                this.orderItinerary.images?.map((img, i) => {
                    return {...img, toDelete: false, order: i + 1}
                }) || []
        }

        get locales() {
            return Object.values(this.$conf.public.locales)
        }

        get request() {
            return {
                duration: this.duration,
                includedInfoDescriptions: this.includedInfoDescriptions,
                notIncludedInfoDescriptions: this.notIncludedInfoDescriptions,
                productDescriptions: this.productDescriptions,
                itineraryDays: this.itineraryDaysDescriptions,
                itineraryImages: this.images.map(img => ({
                    ...(img.fileStream && {fileStream: img.fileStream.split(',')[1]}),
                    ...(img.dayNumber && {requestItineraryDayNumber: +img.dayNumber}),
                    ...(img.imageId && {imageId: img.imageId}),
                    order: +img.order - 1,
                    toDelete: img.toDelete,
                    fileNames: {
                        en: 'image.jpg',
                    },
                })),
                orderId: this.orderId,
            }
        }
    }

    export default EditItineraryModal
</script>
